/**
 * App.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * Root application component. Replaces substrate health-check screen
 * with full branded shell per §3.8.
 *
 * Composition:
 *   TenantProvider → ThemeApplicator → BrowserRouter →
 *     AuthGate → Layout (Sidebar + TopBar) → AppRouter
 *
 * Shell is module-agnostic: zero hard-coded module IDs or routes here.
 * Modules appear when tenantConfig.enabled_modules is populated
 * (ELYON-DEMO-TENANT-CONFIG-1).
 */
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { TenantProvider }  from './shell/TenantContext.jsx';
import { ThemeApplicator } from './shell/ThemeApplicator.jsx';
import { AuthGate }        from './shell/AuthGate.jsx';
import { Layout }          from './shell/Layout.jsx';
import { AppRouter }       from './shell/AppRouter.jsx';
import { AEGISPanel }      from './shell/AEGISPanel.jsx';
import { CommandPalette }  from './shell/CommandPalette.jsx';

import './assets/elyon-tokens.css';
import './shell/shell.css';

// ELYON-COMMAND-CENTER-WORLD-CLASS-FINISH-1: app-root TanStack Query client.
// Module data layers (command center, and future modules) use useQuery; without
// a provider at the root, every useQuery throws "No QueryClient set" and crashes
// the module. Sensible defaults: one retry, no refetch-on-focus (operator
// surfaces shouldn't flicker), 60s default staleness (cards override per query).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 60_000 },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <ThemeApplicator />
        <BrowserRouter>
          <AuthGate>
            <Layout>
              <AppRouter />
            </Layout>
            {/* AEGIS slide-in panel — renders outside Layout so it overlays correctly */}
            <AEGISPanel />
            {/* Command palette — global keyboard ⌘K shortcut */}
            <CommandPalette />
          </AuthGate>
        </BrowserRouter>
      </TenantProvider>
    </QueryClientProvider>
  );
}
